import { useState, type CSSProperties } from 'react';

import { formatTopLevelRef, parseTopLevelRef } from './node-configs/jsonata-ref';

/**
 * Shared Connect/Advanced "mapping row" used by both:
 *
 *   - per-node `inputMapping` editor (`NodeSchemaEditor`)
 *   - workflow-level `outputMapping` editor (`WorkflowSchemaEditor`)
 *
 * The on-disk shapes differ — inputMapping is stored as
 * `Record<string, unknown>` directly, outputMapping is a single JSONata
 * object-construction expression that we round-trip through that same
 * record shape via `jsonata-object-mapping`. This row component
 * operates entirely on the record-of-rows representation; the parent
 * handles serialization to/from disk.
 *
 * A row renders in one of two modes:
 *
 *   - **Connect** (default for empty or top-level-ref values): two
 *     dropdowns — pick a source, then pick a field. The user never
 *     sees `$` or JSONata syntax. We emit canonical refs under the
 *     hood via `formatTopLevelRef`.
 *
 *   - **Advanced**: textbox accepting raw JSONata or any literal.
 *     Used when the value can't be losslessly represented as
 *     source → field, or when the user explicitly opts in.
 */

/** One editable row of a record-style mapping. */
export interface IMappingRow {
  key: string;
  value: string;
}

/**
 * A "source" in Connect mode — something a value can come from. Each
 * source carries its top-level output field names (used to populate
 * the field dropdown).
 */
export interface IConnectSource {
  /** The literal source key persisted in JSONata refs — e.g. `__input` or a nodeKey. */
  sourceKey: string;
  /** Human label shown in the dropdown. */
  label: string;
  /** Top-level field names from the source's declared schema (may be empty). */
  fields: string[];
}

/** Top-level keys of a JSON Schema's `properties`, or [] if none. */
export function topLevelKeys(schema: Record<string, unknown> | null | undefined): string[] {
  if (!schema || typeof schema !== 'object') return [];
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  return Object.keys(props as Record<string, unknown>);
}

// ─── Shared row styles ─────────────────────────────────────────────────
// Exported so the parent's "+ Add" / Clear buttons can match.

export const mappingRowStyle: CSSProperties = {
  display: 'grid',
  // key | value (source+field, or advanced textbox) | Adv toggle | remove
  gridTemplateColumns: 'minmax(120px, 1fr) minmax(0, 2fr) auto auto',
  gap: 6,
  alignItems: 'center',
  padding: 'var(--sp-2)',
  borderBottom: '1px solid var(--border-subtle)',
};

export const mappingMonoInput: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  padding: '4px 6px',
  height: 26,
};

export const mappingSmallBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-subtle)',
  color: 'var(--text-secondary)',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: 11,
  cursor: 'pointer',
  height: 26,
};

/**
 * Convert the stored `Record<string, unknown>` into editable string
 * rows. JSONata refs (strings starting with `$`) are surfaced verbatim;
 * non-string literals are JSON-stringified so they remain editable as
 * text. Used by both input-mapping and workflow-output-mapping
 * authoring.
 */
export function toMappingRows(mapping: Record<string, unknown>): IMappingRow[] {
  return Object.entries(mapping).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

/**
 * Inverse of `toMappingRows`. Strings starting with `$` are stored as-is
 * (JSONata — the input mapper evaluates them); other strings are first
 * attempted as JSON (so users can type `42`, `true`, `null`, `{ "x": 1 }`
 * and get the native value), falling back to the raw string literal.
 */
export function fromMappingRows(rows: IMappingRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    if (r.key.trim() === '') continue;
    const v = r.value;
    if (v.startsWith('$')) {
      out[r.key] = v;
      continue;
    }
    try {
      out[r.key] = JSON.parse(v);
    } catch {
      out[r.key] = v;
    }
  }
  return out;
}

/**
 * One row of a mapping editor. Renders in Connect or Advanced mode
 * based on whether the current value parses as a top-level JSONata
 * ref. Empty values render as Connect (so freshly-added rows aren't
 * dumped into Advanced).
 *
 * Customizable bits via props:
 *   - `keyPlaceholder` — placeholder for the key input (e.g.
 *     "input key" vs "output key")
 *   - `valuePlaceholder` — placeholder for the Advanced-mode textbox
 *   - `sourceTooltip` — title attribute on the source dropdown
 *
 * Connect/Advanced toggle behavior is identical between the two
 * call sites; only labels differ.
 */
export function MappingRow({
  row,
  sources,
  onChange,
  onRemove,
  keyPlaceholder = 'key',
  valuePlaceholder = '$.<nodeKey>.field   or   "literal string"   or   42',
}: {
  row: IMappingRow;
  sources: IConnectSource[];
  onChange: (next: Partial<IMappingRow>) => void;
  onRemove: () => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}) {
  // Parse once per render — the stored value is the source of truth for
  // which mode this row should default to. We DON'T persist mode in row
  // state; it's derived. The exception is when the user explicitly
  // flips to Advanced on a parseable value — we track that intent
  // locally so toggling back isn't surprising.
  //
  // Empty values default to Connect too — otherwise a freshly-added
  // row would land in Advanced (because "" doesn't parse as a ref),
  // forcing the user to discover the toggle just to use the typed UI.
  const parsed = parseTopLevelRef(row.value);
  const isEmpty = row.value === '';
  const [forceAdvanced, setForceAdvanced] = useState(false);
  const canRenderConnect = parsed !== null || isEmpty;
  const mode: 'connect' | 'advanced' = canRenderConnect && !forceAdvanced ? 'connect' : 'advanced';

  // When the user picks a source in Connect mode, default to "(whole
  // output)" rather than the first field — picking a field is an
  // explicit action and a stale auto-pick is worse than no pick.
  const setSource = (sourceKey: string) => {
    if (!sourceKey) {
      onChange({ value: '' });
      return;
    }
    const existingField = parsed?.field ?? null;
    onChange({ value: formatTopLevelRef(sourceKey, existingField) });
  };
  const setField = (field: string) => {
    if (!parsed) return;
    onChange({ value: formatTopLevelRef(parsed.sourceKey, field === '' ? null : field) });
  };

  const currentSource = sources.find((s) => s.sourceKey === parsed?.sourceKey);

  return (
    <div style={mappingRowStyle}>
      <input
        className="input"
        value={row.key}
        onChange={(e) => onChange({ key: e.target.value })}
        placeholder={keyPlaceholder}
        spellCheck={false}
        style={{ ...mappingMonoInput, minWidth: 0 }}
      />
      <div style={{ display: 'flex', gap: 4, minWidth: 0 }}>
        {mode === 'connect' ? (
          <>
            <select
              className="select-native"
              value={parsed?.sourceKey ?? ''}
              onChange={(e) => setSource(e.target.value)}
              style={{ fontSize: 11, height: 26, padding: '2px 4px', flex: 1, minWidth: 0 }}
              title="Where this value comes from"
            >
              <option value="">Choose a source…</option>
              {sources.map((s) => (
                <option key={s.sourceKey} value={s.sourceKey}>
                  {s.label}
                </option>
              ))}
              {/* Preserve a stored source the user can no longer pick
                  (e.g. node was renamed/deleted) so we don't silently
                  flip the row away from what's on disk. */}
              {parsed && !currentSource ? (
                <option value={parsed.sourceKey}>{parsed.sourceKey} (missing)</option>
              ) : null}
            </select>
            <select
              className="select-native"
              value={parsed?.field ?? ''}
              onChange={(e) => setField(e.target.value)}
              disabled={!parsed}
              style={{ fontSize: 11, height: 26, padding: '2px 4px', flex: 1, minWidth: 0 }}
              title={
                currentSource && currentSource.fields.length === 0
                  ? 'This source has no declared output fields. Declare them in that node’s Output Validation, or switch this row to Advanced.'
                  : 'Which field on the source'
              }
            >
              <option value="">(whole output)</option>
              {currentSource?.fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
              {/* Same defensive rendering as the source dropdown: if the
                  stored field isn't in the upstream's declared schema,
                  surface it so it isn't silently dropped. */}
              {parsed?.field && currentSource && !currentSource.fields.includes(parsed.field) ? (
                <option value={parsed.field}>{parsed.field} (not declared)</option>
              ) : null}
            </select>
          </>
        ) : (
          <input
            className="input"
            value={row.value}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder={valuePlaceholder}
            spellCheck={false}
            style={{ ...mappingMonoInput, minWidth: 0, flex: 1 }}
          />
        )}
      </div>
      <button
        type="button"
        style={mappingSmallBtn}
        onClick={() => {
          // Connect ⇄ Advanced. Going to Advanced is always allowed —
          // we just stop trying to render the parsed view. Going BACK
          // to Connect is only allowed when the current value parses.
          if (mode === 'connect') {
            setForceAdvanced(true);
          } else if (canRenderConnect) {
            setForceAdvanced(false);
          }
        }}
        disabled={mode === 'advanced' && !canRenderConnect}
        title={
          mode === 'connect'
            ? 'Switch to Advanced (raw expression) for this field'
            : canRenderConnect
              ? 'Switch back to Connect mode'
              : 'Connect mode only handles simple "source → field" wiring; this value is too complex.'
        }
      >
        {mode === 'connect' ? 'Adv' : '↺'}
      </button>
      <button type="button" style={mappingSmallBtn} onClick={onRemove} title="Remove row">
        ×
      </button>
    </div>
  );
}
